import { useState } from 'react'

import { Box, BoxList, Button, Text } from '@factorialco/deprecated-design-system'
import { AtsGetHiringPipelinePhasesDocument } from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { HiringStageBox } from 'modules/ats/components/HiringStages/HiringStageBox'
import { isHiringStageEditable } from 'modules/ats/lib/utils/hiringStages'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { EditPipelineSidepanel } from './EditPipelineSidepanel'
import { HiringPipelineSkeleton } from './HiringPipelineSkeleton'
import { groupApplicationPhasesByHiringStage } from './utils'

interface HiringTeamProps {
  jobId: string
}

export const HiringPipeline = ({ jobId }: HiringTeamProps) => {
  const [isCustomizingPipeline, setIsCustomizingPipeline] = useState(false)

  const { data } = useQuery(AtsGetHiringPipelinePhasesDocument, {
    variables: { jobId },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccesHiringPipeline)
  }, [])

  const applicationPhases = data?.ats.applicationPhasesConnection.nodes || []
  const hiringStages = data?.ats.hiringStagesConnection.nodes || []

  const groupedStagesWithPhases = groupApplicationPhasesByHiringStage(
    hiringStages,
    applicationPhases
  )
  const hiringStageWithApplicationPhases = groupedStagesWithPhases.filter(
    (stage) => stage.applicationPhases.length > 0
  )

  const hasHiringStages = hiringStageWithApplicationPhases.length > 0

  return (
    <SectionComponentLayout>
      <Box gap="s48">
        <Box gap="s8">
          <Text size="400" color="grey1000" weight="semibold">
            {i18n.t('ats.job_posting_settings.hiring_pipeline.title')}
          </Text>
          <Text size="300" color="grey800">
            {i18n.t('ats.job_posting_settings.hiring_pipeline.description')}
          </Text>
          <HelpCenterLink name="about_the_hiring_pipeline">
            {i18n.t('common.learn_more')}
          </HelpCenterLink>
          <Box paddingTop="s8">
            <Button
              onClick={() => setIsCustomizingPipeline(true)}
              iconName="pencil"
              type="secondary"
            >
              {i18n.t('ats.job_posting_settings.hiring_pipeline.customize')}
            </Button>
          </Box>
        </Box>

        {hasHiringStages ? (
          <Box gap="s16">
            {hiringStageWithApplicationPhases.map((stage) => (
              <HiringStageBox
                key={`stage-item-${stage.id}`}
                stageName={stage.name}
                stageLabel={stage.label}
              >
                {isHiringStageEditable(stage.name) && (
                  <BoxList>
                    {stage.applicationPhases.map((phase) => (
                      <BoxList.Item key={`stage-phase-${phase.id}`}>
                        <Text.Normal weight="medium" capitalize>
                          {phase.name}
                        </Text.Normal>
                      </BoxList.Item>
                    ))}
                  </BoxList>
                )}
              </HiringStageBox>
            ))}
          </Box>
        ) : (
          <HiringPipelineSkeleton />
        )}

        {isCustomizingPipeline && (
          <EditPipelineSidepanel
            stages={groupedStagesWithPhases}
            jobId={jobId}
            onClose={() => setIsCustomizingPipeline(false)}
          />
        )}
      </Box>
    </SectionComponentLayout>
  )
}
